import { useState } from "react";
import classNames from "classnames";

import { useAppSelector } from "../redux/hooks";
import { useGetQuickSearchFeaturesQuery } from "../redux/services/vectormapsAPI";

import { highlightSelectedText } from "../utils/highlightSelectedText";

import ListItemResult from "../components/ListItemResult/ListItemResult";
import { IconType } from "@livingmap/core-ui-v2";
import { QuickSearchFeature } from "../redux/services/types";
import { distance, point } from "@turf/turf";
import { Point } from "geojson";

export const getSuggestionTitleKey = (title: string) =>
  title.trim().toLowerCase();

export const normalizeSearchSuggestionText = (text: string) =>
  text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const matchesSearchSuggestion = (title: string, search: string) => {
  const trimmedSearch = search.trim();

  if (!trimmedSearch) {
    return false;
  }

  const titleLower = title.toLowerCase();
  const searchLower = trimmedSearch.toLowerCase();

  if (titleLower.includes(searchLower)) {
    return true;
  }

  const normalizedSearchRegex = getNormalizedSearchRegex(trimmedSearch);

  if (normalizedSearchRegex) {
    return normalizedSearchRegex.test(title);
  }

  const normalizedSearch = normalizeSearchSuggestionText(trimmedSearch);

  return (
    normalizedSearch !== "" &&
    normalizeSearchSuggestionText(title).includes(normalizedSearch)
  );
};

const getNormalizedSearchRegex = (search: string) => {
  if (!/[^a-z0-9]/i.test(search)) {
    return null;
  }

  const tokens = search.match(/[a-z0-9]+/gi);

  if (!tokens?.length) {
    return null;
  }

  const startsWithSeparator = /^[^a-z0-9]+/i.test(search);
  const endsWithSeparator = /[^a-z0-9]+$/i.test(search);
  const tokenPattern = tokens.map(escapeRegExp).join("[^a-z0-9]+");

  return new RegExp(
    `${startsWithSeparator ? "[^a-z0-9]+" : ""}${tokenPattern}${
      endsWithSeparator ? "[^a-z0-9]+" : ""
    }`,
    "i",
  );
};

const escapeRegExp = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

interface Props {
  limit?: number;
  className?: string;
  highlightedIndex?: number;
  highlightedClassName?: string;
  position?: {
    latitude: number;
    longitude: number;
  } | null;
  floorId?: number;
  icon?: IconType;
  handleOnClick: (value: string) => void;
}

export const useSearchSuggestions = ({
  limit,
  className,
  highlightedIndex,
  highlightedClassName,
  handleOnClick,
  position,
  floorId,
  icon,
}: Props) => {
  const { project, language } = useAppSelector((state) => state.application);

  const [searchSuggestion, setSearchSuggestion] = useState("");
  const uniqueSuggestionTitles = new Set<string>();

  const { data: quickSearchFeatureList } = useGetQuickSearchFeaturesQuery(
    { project, lang: language },
    { skip: !project },
  );
  const searchSuggestionItems = quickSearchFeatureList
    ? quickSearchFeatureList.data
        .filter((quickFeature) =>
          matchesSearchSuggestion(quickFeature.title, searchSuggestion),
        )
        .sort((a, b) => {
          if (floorId && position) {
            const a_dist = distance(
              a.locationCenter as Point,
              point([position.longitude, position.latitude]),
            );
            const a_rank = a_dist + (a.floorId === floorId ? 0.5 : 1);
            const b_dist = distance(
              b.locationCenter as Point,
              point([position.longitude, position.latitude]),
            );
            const b_rank = b_dist + (b.floorId === floorId ? 0.5 : 1);
            return a_rank - b_rank;
          } else {
            // no positional data so do not sort
            return 0;
          }
        })
        .filter((quickFeature) => {
          const titleKey = getSuggestionTitleKey(quickFeature.title);

          if (uniqueSuggestionTitles.has(titleKey)) {
            return false;
          }

          uniqueSuggestionTitles.add(titleKey);
          return true;
        })
        .slice(0, limit || quickSearchFeatureList.data.length)
    : [];

  const searchSuggestionsList = searchSuggestionItems.map(
    (value: QuickSearchFeature, index: number) => {
      const isActive = highlightedIndex === index;
      const displayIcon = icon ? icon : "SearchIcon";
      return (
        <ListItemResult
          key={getSuggestionTitleKey(value.title)}
          className={classNames(className, {
            [highlightedClassName!]: isActive,
          })}
          title={highlightSelectedText(searchSuggestion, value.title)}
          dataQA="search-suggestion-item"
          icon={displayIcon}
          onClick={() => {
            handleOnClick(value.title);
          }}
        />
      );
    },
  );

  return {
    searchSuggestion,
    setSearchSuggestion,
    searchSuggestionsList,
  };
};
